/**
 * OCTOPUS - LoadCurveEditor
 * Editor da LOAD CURVE de plena carga de um motor (feature catálogo, dono 2026-07-08).
 *
 * Três modos determinísticos (SEM LLM) de montar a curva {rpm, max_lph}:
 *  - Tabela: digita RPM | L/h ponto a ponto.
 *  - Importar CSV: cola/importa `rpm,max_lph` (ou `rpm,power_kw,sfc_g_kwh` no modo cálculo).
 *  - Cálculo assistido: digita RPM | kW (ou torque) | g/kWh + densidade; o sistema calcula
 *    max_lph = kW × g/kWh ÷ densidade (a fórmula do spec sheet, substitui a leitura por IA).
 *
 * Controlado: `value` = [{rpm, max_lph}], `onChange(curve)`. Preview em mini-gráfico inline.
 */
import React, { useState, useMemo } from 'react';
import { Plus, Trash2, Calculator, Table2, ClipboardPaste } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const DEFAULT_DENSITY = 840; // g/L — padrão ISO/Volvo (rodapé do spec sheet)

// max_lph = potência(kW) × SFC(g/kWh) ÷ densidade(g/L). Potência via torque se faltar kW.
const powerKw = (p) => {
  if (p.power_kw != null && p.power_kw !== '') return Number(p.power_kw);
  if (p.torque_nm != null && p.torque_nm !== '' && p.rpm) {
    return (Number(p.torque_nm) * 2 * Math.PI * Number(p.rpm)) / 60 / 1000;
  }
  return null;
};

const computeCurve = (points, density) => {
  const d = Number(density) || DEFAULT_DENSITY;
  return points
    .map((p) => {
      const rpm = Number(p.rpm);
      const sfc = Number(p.sfc_g_kwh);
      const kw = powerKw(p);
      if (!rpm || rpm <= 0 || !sfc || sfc <= 0 || !kw || kw <= 0) return null;
      return { rpm: Math.round(rpm * 10) / 10, max_lph: Math.round((kw * sfc) / d * 100) / 100 };
    })
    .filter(Boolean)
    .sort((a, b) => a.rpm - b.rpm);
};

// Validação espelha backend motor_regime.validate_load_curve (avisos não bloqueiam).
const validateCurve = (curve) => {
  const errors = [];
  const warnings = [];
  const seen = new Set();
  const pts = [];
  for (const it of curve) {
    const r = Number(it.rpm);
    const v = Number(it.max_lph);
    if (!Number.isFinite(r) || !Number.isFinite(v) || r <= 0 || v <= 0) continue;
    if (seen.has(r)) { errors.push(`RPM duplicado: ${r}`); continue; }
    seen.add(r);
    pts.push({ rpm: r, max_lph: v });
  }
  pts.sort((a, b) => a.rpm - b.rpm);
  if (pts.length < 2) errors.push('Curva precisa de ao menos 2 pontos válidos.');
  for (let i = 1; i < pts.length; i++) {
    if (pts[i].max_lph < pts[i - 1].max_lph) {
      warnings.push(`L/h cai de ${pts[i - 1].max_lph} p/ ${pts[i].max_lph} entre ${pts[i - 1].rpm} e ${pts[i].rpm} rpm — a curva de plena carga deveria ser não-decrescente (confira a transcrição).`);
    }
  }
  return { ok: errors.length === 0, points: pts, errors, warnings };
};

// Mini-gráfico SVG (sem libs): polilinha normalizada L/h × rpm.
const CurvePreview = ({ curve }) => {
  const pts = [...(curve || [])].filter((p) => p.rpm > 0 && p.max_lph > 0).sort((a, b) => a.rpm - b.rpm);
  if (pts.length < 2) return null;
  const W = 260, H = 90, pad = 6;
  const xs = pts.map((p) => p.rpm), ys = pts.map((p) => p.max_lph);
  const xmin = Math.min(...xs), xmax = Math.max(...xs);
  const ymax = Math.max(...ys), ymin = 0;
  const sx = (x) => pad + ((x - xmin) / (xmax - xmin || 1)) * (W - 2 * pad);
  const sy = (y) => H - pad - ((y - ymin) / (ymax - ymin || 1)) * (H - 2 * pad);
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${sx(p.rpm).toFixed(1)},${sy(p.max_lph).toFixed(1)}`).join(' ');
  return (
    <svg width={W} height={H} className="rounded border border-border bg-muted/20" role="img" aria-label="Prévia da curva">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.5" className="text-primary" />
      {pts.map((p, i) => (
        <circle key={i} cx={sx(p.rpm)} cy={sy(p.max_lph)} r="2" className="fill-primary" />
      ))}
      <text x={pad} y={H - 1} className="fill-muted-foreground" fontSize="8">{xmin} rpm</text>
      <text x={W - pad} y={H - 1} textAnchor="end" className="fill-muted-foreground" fontSize="8">{xmax} rpm · {ymax} L/h</text>
    </svg>
  );
};

const LoadCurveEditor = ({ value, onChange }) => {
  const [mode, setMode] = useState('table'); // 'table' | 'assist'
  const [assistPoints, setAssistPoints] = useState([]); // [{rpm, power_kw, torque_nm, sfc_g_kwh}]
  const [assistInput, setAssistInput] = useState('torque'); // 'power' | 'torque'
  const [density, setDensity] = useState(DEFAULT_DENSITY);

  const curve = useMemo(() => value || [], [value]);
  const validation = useMemo(() => validateCurve(curve), [curve]);

  const setCurve = (next) => onChange(next);
  const patchRow = (idx, changes) => setCurve(curve.map((r, i) => (i === idx ? { ...r, ...changes } : r)));
  const addRow = () => setCurve([...curve, { rpm: '', max_lph: '' }]);
  const removeRow = (idx) => setCurve(curve.filter((_, i) => i !== idx));

  // CSV: aceita "rpm,max_lph" (modo tabela) — cabeçalho opcional é ignorado.
  const importCsv = (text) => {
    const rows = [];
    for (const line of text.split(/\r?\n/)) {
      const cols = line.split(/[,;\t]/).map((c) => c.trim());
      if (cols.length < 2) continue;
      const rpm = Number(cols[0]);
      const lph = Number(cols[1]);
      if (Number.isFinite(rpm) && Number.isFinite(lph) && rpm > 0 && lph > 0) {
        rows.push({ rpm, max_lph: lph });
      }
    }
    if (rows.length) setCurve(rows.sort((a, b) => a.rpm - b.rpm));
  };

  const runAssist = () => {
    const computed = computeCurve(assistPoints, density);
    if (computed.length) setCurve(computed);
  };

  const numVal = (v) => (v === null || v === undefined ? '' : v);

  return (
    <div className="space-y-2 rounded-md border border-border/60 p-2 bg-muted/10">
      {/* Toggle de modo */}
      <div className="flex items-center gap-1.5">
        <Button type="button" size="sm" variant={mode === 'table' ? 'default' : 'ghost'}
          className="h-7 text-xs gap-1" onClick={() => setMode('table')}>
          <Table2 className="h-3.5 w-3.5" /> Tabela (L/h)
        </Button>
        <Button type="button" size="sm" variant={mode === 'assist' ? 'default' : 'ghost'}
          className="h-7 text-xs gap-1" onClick={() => setMode('assist')}>
          <Calculator className="h-3.5 w-3.5" /> Cálculo assistido
        </Button>
        <div className="flex-1" />
        <Button asChild type="button" size="sm" variant="ghost" className="h-7 text-xs gap-1">
          <label className="cursor-pointer" title="Importar rpm,max_lph de um CSV — substitui os pontos atuais">
            <ClipboardPaste className="h-3.5 w-3.5" /> Importar CSV
            <input type="file" accept=".csv,text/csv" className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) f.text().then(importCsv);
                e.target.value = '';
              }} />
          </label>
        </Button>
      </div>

      {mode === 'table' && (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground px-1">
            <span className="w-20">RPM</span><span className="w-24">L/h (plena carga)</span>
          </div>
          {curve.map((row, idx) => (
            <div key={idx} className="flex items-center gap-1.5">
              <Input className="h-7 w-20 text-xs" type="number" placeholder="rpm"
                value={numVal(row.rpm)} onChange={(e) => patchRow(idx, { rpm: e.target.value === '' ? '' : Number(e.target.value) })} />
              <Input className="h-7 w-24 text-xs" type="number" placeholder="L/h"
                value={numVal(row.max_lph)} onChange={(e) => patchRow(idx, { max_lph: e.target.value === '' ? '' : Number(e.target.value) })} />
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                title="Remover ponto" aria-label="Remover ponto"
                onClick={() => removeRow(idx)}><Trash2 className="h-3 w-3" /></Button>
            </div>
          ))}
          <Button type="button" variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={addRow}>
            <Plus className="h-3 w-3" /> Ponto
          </Button>
        </div>
      )}

      {mode === 'assist' && (
        <div className="space-y-1.5">
          <p className="text-[11px] text-muted-foreground">
            Transcreva os pontos do spec sheet (plena carga). L/h = {assistInput === 'torque' ? 'torque×2π×rpm/60' : 'potência'} × g/kWh ÷ densidade.
          </p>
          <div className="flex items-center gap-1.5">
            <Button type="button" size="sm" variant={assistInput === 'torque' ? 'secondary' : 'ghost'}
              className="h-6 text-[11px]" onClick={() => setAssistInput('torque')}>Torque (Nm)</Button>
            <Button type="button" size="sm" variant={assistInput === 'power' ? 'secondary' : 'ghost'}
              className="h-6 text-[11px]" onClick={() => setAssistInput('power')}>Potência (kW)</Button>
            <div className="flex-1" />
            <span className="text-[10px] text-muted-foreground">densidade g/L</span>
            <Input className="h-6 w-16 text-xs" type="number" value={density}
              onChange={(e) => setDensity(e.target.value)} />
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground px-1">
            <span className="w-16">RPM</span>
            <span className="w-20">{assistInput === 'torque' ? 'Torque Nm' : 'Potência kW'}</span>
            <span className="w-16">g/kWh</span>
          </div>
          {assistPoints.map((row, idx) => {
            const patch = (ch) => setAssistPoints(assistPoints.map((r, i) => (i === idx ? { ...r, ...ch } : r)));
            return (
              <div key={idx} className="flex items-center gap-1.5">
                <Input className="h-7 w-16 text-xs" type="number" placeholder="rpm"
                  value={numVal(row.rpm)} onChange={(e) => patch({ rpm: e.target.value })} />
                <Input className="h-7 w-20 text-xs" type="number"
                  placeholder={assistInput === 'torque' ? 'Nm' : 'kW'}
                  value={numVal(assistInput === 'torque' ? row.torque_nm : row.power_kw)}
                  onChange={(e) => patch(assistInput === 'torque' ? { torque_nm: e.target.value } : { power_kw: e.target.value })} />
                <Input className="h-7 w-16 text-xs" type="number" placeholder="g/kWh"
                  value={numVal(row.sfc_g_kwh)} onChange={(e) => patch({ sfc_g_kwh: e.target.value })} />
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  title="Remover ponto" aria-label="Remover ponto"
                  onClick={() => setAssistPoints(assistPoints.filter((_, i) => i !== idx))}><Trash2 className="h-3 w-3" /></Button>
              </div>
            );
          })}
          <div className="flex items-center gap-1.5">
            <Button type="button" variant="ghost" size="sm" className="h-7 text-xs gap-1"
              onClick={() => setAssistPoints([...assistPoints, { rpm: '', power_kw: '', torque_nm: '', sfc_g_kwh: '' }])}>
              <Plus className="h-3 w-3" /> Ponto
            </Button>
            <Button type="button" variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={runAssist}
              title="Calcula max_lph de cada ponto e SUBSTITUI a tabela atual">
              <Calculator className="h-3.5 w-3.5" /> Calcular curva
            </Button>
            <span className="text-[10px] text-muted-foreground">→ substitui a tabela em L/h (editável)</span>
          </div>
        </div>
      )}

      {/* Preview + validação */}
      {curve.length >= 2 && (
        <div className="flex flex-wrap items-start gap-3 pt-1">
          <CurvePreview curve={curve} />
          <div className="text-[11px] space-y-0.5 flex-1 min-w-[140px]">
            <p className="text-muted-foreground">{validation.points.length} ponto(s) válido(s)</p>
            {validation.errors.map((e, i) => <p key={i} className="text-destructive">⚠ {e}</p>)}
            {validation.warnings.map((w, i) => <p key={i} className="text-yellow-500">⚠ {w}</p>)}
            {validation.ok && validation.warnings.length === 0 && <p className="text-emerald-500">✓ curva válida</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoadCurveEditor;
